import React, { FC, forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '../../../_core/components/icons';
import { useOpenSurveyModal } from '../../hooks/modal-forms/use-open-view-modal.hook';
import { SurveyTranslateQuestionsArray } from './translate-questions-array.component';

export type TranslateSurveyTemplateFormProps = {
	currentLanguage: string;
	defaultValues?: any;
	setAllFormsValues?: any;
};

export type TranslateSurveyTemplateFormHandle = {
	isDirty: boolean;
};

export const TranslateSurveyTemplateForm = forwardRef<
	TranslateSurveyTemplateFormHandle,
	TranslateSurveyTemplateFormProps
>((props, ref) => {
	const [t] = useTranslation();
	const { defaultValues, currentLanguage, setAllFormsValues } = props;

	const methods = useForm({
		defaultValues,
		mode: 'all',
	});

	const {
		getValues,
		control,
		reset,
		formState: { isDirty, isSubmitting },
	} = methods;

	useImperativeHandle(ref, () => ({
		isDirty,
		isSubmitting,
		resetForm: () => reset(),
	}));

	const watchedItems = useWatch({
		name: 'questions',
		control,
	});

	useEffect(() => {
		const formFinished = getValues('questions')?.every((question: any) =>
			question?.options?.length > 0
				? question.translatedText?.trim() &&
				  question?.options?.length > 0 &&
				  question?.options?.every((option: any) => option.translatedText?.trim())
				: question.translatedText?.trim()
		);
		setAllFormsValues((prev: any) => ({
			...prev,
			[currentLanguage]: { ...prev[currentLanguage], isFinished: formFinished, questions: getValues('questions') },
		}));
	}, [watchedItems]);

	useEffect(() => {
		reset(defaultValues);
	}, [currentLanguage]);

	const currentQuestions = getValues('questions');

	const openSurveyModal = useOpenSurveyModal({
		modalTitle: '',
		questions: currentQuestions,
	});

	return (
		<FormProvider {...methods}>
			<Box component="form" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						borderBottom: '1px solid lightgrey',
						paddingBottom: 3,
					}}
				>
					<Typography sx={{ fontSize: '24px', fontWeight: 400 }}>{`${currentLanguage} language`}</Typography>
					<CoreButton
						variant="info-light"
						circle
						size="large"
						tooltipTitle={t('buttons.tooltips.survey-preview')}
						onClick={openSurveyModal}
					>
						<EyeIcon sx={{ fontSize: 30 }} />
					</CoreButton>
				</Box>
				<SurveyTranslateQuestionsArray />
			</Box>
		</FormProvider>
	);
});
